import React, { useState, useEffect, useMemo } from 'react';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, AreaChart, Area, PieChart, Pie, Cell,
  ComposedChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { 
  Grid, Card, Typography, Box, CircularProgress, useTheme, 
  MenuItem, Select, FormControl, InputLabel, Chip, Stack, Divider,
  IconButton, Avatar, Button, Paper, alpha, Badge, Fade, Zoom,
  LinearProgress, Alert, AlertTitle, Tooltip as MuiTooltip, useMediaQuery
} from '@mui/material';
import { 
  AttachMoney, Receipt, AccountBalance, ShowChart, TrendingUp,
  RestaurantMenu, People, ShoppingCart, Inventory, LocalShipping,
  Paid, Savings, AccountBalanceWallet, Analytics as AnalyticsIcon,
  DateRange, PieChart as PieChartIcon, Equalizer, Download,
  Print, Share, Refresh, FilterList, CalendarToday,
  TrendingDown, TrendingUp as TrendingUpIcon, Warning,
  CheckCircle, Info, Star, Speed, Timeline, BarChart as BarChartIcon,
  ArrowUpward, ArrowDownward, RemoveRedEye, Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import { auth } from "../Firebase/Config";
import AddRestaurantFormPopup from '../Components/PopUpAddResto';
import { styled } from '@mui/system';
import RapportX from '../Services/RapportX';
import RH from '../Services/RH';
import Facture from '../Services/FactureFournisseur';
import EncaissementService from "../Services/Encaissement";
import DecaissementService from "../Services/Decaissement";
import { motion, AnimatePresence } from "framer-motion";
import { format, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const StyledCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  borderRadius: '24px',
  background: '#FFFFFF',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0,0,0,0.1)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover::before': {
    opacity: 1,
  }
}));

const MetricCard = styled(Paper)(({ theme, gradient }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  borderRadius: '20px',
  background: gradient || '#FFFFFF',
  boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
  transition: 'all 0.3s ease',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 20px 30px rgba(0,0,0,0.1)',
  }
}));

const StatIconWrapper = styled(Avatar)(({ theme, color }) => ({
  width: 48,
  height: 48,
  [theme.breakpoints.up('sm')]: {
    width: 56,
    height: 56,
  },
  background: `linear-gradient(135deg, ${color || theme.palette.primary.main}, ${alpha(color || theme.palette.primary.main, 0.7)})`,
  boxShadow: `0 8px 16px ${alpha(color || theme.palette.primary.main, 0.3)}`,
  color: 'white',
}));

const CustomTooltip = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2),
  },
  borderRadius: '12px',
  background: '#1A2B3C',
  color: 'white',
  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
  border: '1px solid rgba(255,255,255,0.1)',
  minWidth: 180,
  [theme.breakpoints.up('sm')]: {
    minWidth: 220,
  },
}));

const chartColors = [
  '#4F46E5', '#10B981', '#F59E0B', '#EF4444', 
  '#8B5CF6', '#EC4899', '#06B6D4', '#14B8A6'
];

function AnalyticsPage({ sidebarOpen }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [encaissements, setEncaissements] = useState([]);
  const [decaissements, setDecaissements] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalDesimpyé, setTotalDesimpyé] = useState(0);
  const [rapportXValues, setRapportXValues] = useState({});
  const [totalRapportX, setTotalRapportX] = useState(0);
  const [totalHTVA, setTotalHTVA] = useState(0);
  const [totalTTC, setTotalTTC] = useState(0);
  const [TotalTVADeductible, setTotalTVADeductible] = useState(0);
  const [totalSalary, setTotalSalary] = useState(0);
  const [totalAccompte, setTotalAccompte] = useState(null);
  const user = JSON.parse(localStorage.getItem("user"));
  const restaurantId = localStorage.getItem("restaurantId");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [viewMode, setViewMode] = useState('daily'); // daily, weekly, monthly

  // Responsive padding based on sidebar
  const mainContentPadding = {
    px: sidebarOpen ? (isMobile ? 1 : 2) : 2,
    pt: isMobile ? 2 : 3,
    transition: theme.transitions.create(['padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    })
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId) return;
      setLoading(true);
      
      try {
        const [encaissementsData, decaissementsData] = await Promise.all([
          EncaissementService.getAllEncaissements(restaurantId),
          DecaissementService.getAllDecaissements(restaurantId)
        ]);
        
        const filteredEncaissements = encaissementsData.filter(
          e => e.restaurantId === restaurantId
        );
        const filteredDecaissements = decaissementsData.filter(
          d => d.restaurantId === restaurantId
        );

        setEncaissements(filteredEncaissements);
        setDecaissements(filteredDecaissements);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [restaurantId]);

  useEffect(() => {
    const fetchRapportX = async () => {
      if (!restaurantId) return;
      try {
        const allRapportX = await RapportX.getAllRapportX(restaurantId);
        const filteredRapportX = allRapportX
          .filter(item => item.restaurantId === restaurantId)
          .filter(item => {
            const itemDate = new Date(item.date);
            return (
              itemDate.getMonth() === selectedMonth &&
              itemDate.getFullYear() === selectedYear
            );
          });
        
        const mappedValues = filteredRapportX.reduce((acc, item) => {
          acc[item.date] = item.value;
          return acc;
        }, {});
        
        setRapportXValues(mappedValues);
        setTotalRapportX(filteredRapportX.reduce((sum, item) => sum + Number(item.value), 0));
      } catch (error) {
        console.error("Error fetching RapportX:", error);
      }
    };
  
    fetchRapportX();
  }, [restaurantId, selectedMonth, selectedYear]);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const db = getFirestore();
        const q = query(collection(db, 'restaurants'), 
          where('ownerId', '==', user?.uid));
        const querySnapshot = await getDocs(q);
        const restaurantData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        
        setRestaurants(restaurantData);
        if (restaurantData.length > 0 && !restaurantId) {
          localStorage.setItem('restaurantId', restaurantData[0].id);
        }
      } catch (error) {
        console.error("Error fetching restaurants:", error);
      }
    };
  
    if (user?.uid) fetchRestaurants();
  }, [user?.uid, restaurantId]);

  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        if (!restaurantId) return;
  
        const [htva, ttc, tvaDeductible, salary, accompte] = await Promise.all([
          Facture.getTotalAchatHTVAByRestaurantId(restaurantId, selectedMonth, selectedYear),
          Facture.getTotalTTCByRestaurantId(restaurantId, selectedMonth, selectedYear),
          Facture.getTotalTVADeductibleByRestaurantId(restaurantId, selectedMonth, selectedYear),
          RH.getTotalSalaryByRestaurantId(restaurantId, selectedMonth, selectedYear),
          RH.getTotalAccompteByRestaurantId(restaurantId, selectedMonth, selectedYear)
        ]);
  
        setTotalHTVA(htva);
        setTotalTTC(ttc);
        setTotalTVADeductible(tvaDeductible);
        setTotalSalary(salary);
        setTotalAccompte(accompte);
      } catch (error) {
        console.error("Error fetching financial data:", error);
      }
    };
  
    fetchFinancialData();
  }, [restaurantId, selectedMonth, selectedYear]);

  const processFinancialData = useMemo(() => {
    if (!restaurantId) return [];
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    
    const filteredDecaissements = decaissements.filter(
      d => d.restaurantId === restaurantId
    );

    const dailyData = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      const dateKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      const revenue = rapportXValues[dateKey] || 0;
      const expenses = filteredDecaissements
        .filter(d => new Date(d.date).toISOString().split('T')[0] === dateKey)
        .reduce((sum, d) => sum + (d.value || 0), 0);
      
      return {
        day: day,
        date: dateKey,
        revenue,
        expenses,
        profit: revenue - expenses,
        margin: revenue > 0 ? ((revenue - expenses) / revenue * 100) : 0
      };
    });

    // Weekly aggregation
    const weeklyData = [];
    for (let i = 0; i < dailyData.length; i += 7) {
      const weekData = dailyData.slice(i, i + 7);
      const weekRevenue = weekData.reduce((sum, d) => sum + d.revenue, 0);
      const weekExpenses = weekData.reduce((sum, d) => sum + d.expenses, 0);
      weeklyData.push({
        week: `Semaine ${Math.floor(i / 7) + 1}`,
        revenue: weekRevenue,
        expenses: weekExpenses,
        profit: weekRevenue - weekExpenses,
        avgMargin: weekRevenue > 0 ? ((weekRevenue - weekExpenses) / weekRevenue * 100) : 0
      });
    }

    return {
      daily: dailyData,
      weekly: weeklyData,
      monthly: [{
        month: format(new Date(selectedYear, selectedMonth), 'MMMM yyyy', { locale: fr }),
        revenue: dailyData.reduce((sum, d) => sum + d.revenue, 0),
        expenses: dailyData.reduce((sum, d) => sum + d.expenses, 0),
        profit: dailyData.reduce((sum, d) => sum + d.profit, 0)
      }]
    };
  }, [rapportXValues, decaissements, selectedMonth, selectedYear, restaurantId]);

  const financialKPIs = useMemo(() => {
    const currentData = processFinancialData[viewMode] || [];
    const totalRevenue = currentData.reduce((sum, d) => sum + d.revenue, 0);
    const totalExpenses = currentData.reduce((sum, d) => sum + d.expenses, 0);
    const totalProfit = totalRevenue - totalExpenses;
    
    // Previous period comparison
    const prevMonth = selectedMonth === 0 ? 11 : selectedMonth - 1;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;
    
    return {
      totalRevenue,
      totalExpenses,
      totalProfit,
      avgMargin: totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0,
      peakRevenueDay: processFinancialData.daily?.reduce((max, d) => d.revenue > (max?.revenue || 0) ? d : max, { revenue: 0, day: 0 }),
      bestProfitDay: processFinancialData.daily?.reduce((max, d) => d.profit > (max?.profit || 0) ? d : max, { profit: 0, day: 0 }),
      transactionCount: processFinancialData.daily?.reduce((sum, d) => sum + (d.revenue > 0 ? 1 : 0), 0) || 0,
    };
  }, [processFinancialData, viewMode, selectedMonth, selectedYear]);

  const expenseCategories = useMemo(() => {
    if (!restaurantId) return [];
    return [
      { name: 'Salaires', value: totalSalary, icon: <People />, color: chartColors[0] },
      { name: 'Fournitures', value: totalHTVA, icon: <Inventory />, color: chartColors[1] },
      { name: 'TVA', value: TotalTVADeductible, icon: <Receipt />, color: chartColors[2] },
      { name: 'Autres', value: Math.max(financialKPIs.totalExpenses - (totalSalary + totalHTVA + TotalTVADeductible), 0), icon: <LocalShipping />, color: chartColors[3] }
    ].filter(category => category.value > 0);
  }, [totalSalary, totalHTVA, TotalTVADeductible, financialKPIs, restaurantId]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatCompactCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const renderTooltipContent = ({ active, payload, label }) => {
    if (!active || !payload) return null;

    return (
      <CustomTooltip elevation={3}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: 'white', borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 1 }}>
          {viewMode === 'daily' ? `Jour ${label}` : label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.5
          }}>
            <Box sx={{
              width: 10,
              height: 10,
              backgroundColor: entry.color,
              borderRadius: '4px',
            }}/>
            <Typography variant="caption" sx={{ flex: 1, color: 'rgba(255,255,255,0.7)' }}>
              {entry.name}:
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'white' }}>
              {formatCurrency(entry.value)}
            </Typography>
          </Box>
        ))}
        {payload.length > 1 && (
          <Box sx={{ 
            mt: 1, 
            pt: 1, 
            borderTop: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Bénéfice:
            </Typography>
            <Typography variant="caption" sx={{ 
              fontWeight: 700,
              color: (payload[0]?.value - payload[1]?.value) >= 0 ? '#4CAF50' : '#f44336'
            }}>
              {formatCurrency(payload[0]?.value - payload[1]?.value)}
            </Typography>
          </Box>
        )}
      </CustomTooltip>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        ...mainContentPadding,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Paper sx={{ p: isMobile ? 3 : 4, textAlign: 'center', borderRadius: 4 }}>
          <CircularProgress size={isMobile ? 40 : 60} thickness={4} sx={{ mb: 3 }} />
          <Typography variant={isMobile ? "body1" : "h6"} sx={{ color: '#5F6B7A' }}>
            Chargement de vos données analytiques...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!restaurantId) {
    return (
      <Box sx={{ 
        ...mainContentPadding,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Zoom in={true}>
          <Paper sx={{ 
            p: isMobile ? 3 : 6, 
            textAlign: 'center', 
            borderRadius: 4,
            maxWidth: isMobile ? '90%' : 500,
            background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
            border: '1px solid #E0E0E0'
          }}>
            <AnalyticsIcon sx={{ fontSize: isMobile ? 60 : 80, color: '#9AA6B2', mb: 2 }} />
            <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1, color: '#1A2B3C' }}>
              Aucune donnée disponible
            </Typography>
            <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3 }}>
              Sélectionnez un restaurant pour voir vos analyses financières
            </Typography>
            <AddRestaurantFormPopup />
          </Paper>
        </Zoom>
      </Box>
    );
  }

  const chartData = processFinancialData[viewMode] || [];

  return (
    <Box sx={{ 
      ...mainContentPadding,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background Decoration */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(79,70,229,0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp}>
            <Paper sx={{ 
              mb: isMobile ? 2 : 4,
              p: isMobile ? 2 : 3,
              borderRadius: isMobile ? 3 : 4,
              background: 'linear-gradient(135deg, #1E2A3A 0%, #2C3E50 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: isMobile ? 2 : 2 
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    width: isMobile ? 40 : 56, 
                    height: isMobile ? 40 : 56 
                  }}>
                    <AnalyticsIcon sx={{ fontSize: isMobile ? 24 : 32 }} />
                  </Avatar>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 700, mb: 0.5 }}>
                      Tableau de Bord Analytique
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Chip
                        icon={<CalendarToday sx={{ fontSize: 12 }} />}
                        label={format(new Date(selectedYear, selectedMonth), 'MMMM yyyy', { locale: fr })}
                        size="small"
                        sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                      />
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {['daily', 'weekly', 'monthly'].map((mode) => (
                          <Chip
                            key={mode}
                            label={mode === 'daily' ? (isMobile ? 'Jour' : 'Quotidien') : mode === 'weekly' ? (isMobile ? 'Sem' : 'Hebdomadaire') : (isMobile ? 'Mois' : 'Mensuel')}
                            size="small"
                            onClick={() => setViewMode(mode)}
                            sx={{
                              bgcolor: viewMode === mode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                              color: 'white',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }
                            }}
                          />
                        ))}
                      </Box>
                    </Stack>
                  </Box>
                </Box>

                <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2} sx={{ width: isMobile ? '100%' : 'auto' }}>
                  <FormControl size="small" sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    borderRadius: 2,
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <MenuItem key={i} value={i}>
                          {format(new Date(2000, i), isMobile ? 'MMM' : 'MMMM', { locale: fr })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ 
                    bgcolor: 'rgba(255,255,255,0.1)', 
                    borderRadius: 2,
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                    >
                      {Array.from({ length: 5 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return <MenuItem key={year} value={year}>{year}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            </Paper>
          </motion.div>

          {/* Key Metrics Grid */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard
                  onMouseEnter={() => setHoveredCard('revenue')}
                  onMouseLeave={() => setHoveredCard(null)}
                  gradient="linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)"
                  sx={{
                    transform: hoveredCard === 'revenue' && !isMobile ? 'translateY(-6px)' : 'none',
                    boxShadow: hoveredCard === 'revenue' && !isMobile ? '0 20px 30px rgba(79,70,229,0.3)' : '0 8px 24px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isMobile ? 1 : 2 }}>
                      <Typography variant={isMobile ? "caption" : "body2"} sx={{ opacity: 0.8 }}>Chiffre d'affaires</Typography>
                      <AttachMoney sx={{ opacity: 0.8, fontSize: isMobile ? 18 : 24 }} />
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: isMobile ? 0.5 : 1 }}>
                      {formatCompactCurrency(financialKPIs.totalRevenue)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp sx={{ fontSize: isMobile ? 14 : 16 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        vs mois dernier
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard
                  onMouseEnter={() => setHoveredCard('expenses')}
                  onMouseLeave={() => setHoveredCard(null)}
                  gradient="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                  sx={{
                    transform: hoveredCard === 'expenses' && !isMobile ? 'translateY(-6px)' : 'none',
                    boxShadow: hoveredCard === 'expenses' && !isMobile ? '0 20px 30px rgba(245,158,11,0.3)' : '0 8px 24px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isMobile ? 1 : 2 }}>
                      <Typography variant={isMobile ? "caption" : "body2"} sx={{ opacity: 0.8 }}>Dépenses totales</Typography>
                      <LocalShipping sx={{ opacity: 0.8, fontSize: isMobile ? 18 : 24 }} />
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: isMobile ? 0.5 : 1 }}>
                      {formatCompactCurrency(financialKPIs.totalExpenses)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingDown sx={{ fontSize: isMobile ? 14 : 16 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        {((financialKPIs.totalExpenses / financialKPIs.totalRevenue) * 100).toFixed(1)}% des revenus
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard
                  onMouseEnter={() => setHoveredCard('profit')}
                  onMouseLeave={() => setHoveredCard(null)}
                  gradient={financialKPIs.totalProfit >= 0 
                    ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                    : "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"}
                  sx={{
                    transform: hoveredCard === 'profit' && !isMobile ? 'translateY(-6px)' : 'none',
                    boxShadow: hoveredCard === 'profit' && !isMobile ? '0 20px 30px rgba(16,185,129,0.3)' : '0 8px 24px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isMobile ? 1 : 2 }}>
                      <Typography variant={isMobile ? "caption" : "body2"} sx={{ opacity: 0.8 }}>Bénéfice net</Typography>
                      {financialKPIs.totalProfit >= 0 ? <TrendingUp sx={{ fontSize: isMobile ? 18 : 24 }} /> : <TrendingDown sx={{ fontSize: isMobile ? 18 : 24 }} />}
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: isMobile ? 0.5 : 1 }}>
                      {formatCompactCurrency(financialKPIs.totalProfit)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Marge: {financialKPIs.avgMargin.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <MetricCard
                  onMouseEnter={() => setHoveredCard('transactions')}
                  onMouseLeave={() => setHoveredCard(null)}
                  gradient="linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)"
                  sx={{
                    transform: hoveredCard === 'transactions' && !isMobile ? 'translateY(-6px)' : 'none',
                    boxShadow: hoveredCard === 'transactions' && !isMobile ? '0 20px 30px rgba(139,92,246,0.3)' : '0 8px 24px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: isMobile ? 1 : 2 }}>
                      <Typography variant={isMobile ? "caption" : "body2"} sx={{ opacity: 0.8 }}>Jours d'activité</Typography>
                      <CalendarToday sx={{ opacity: 0.8, fontSize: isMobile ? 18 : 24 }} />
                    </Box>
                    <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: isMobile ? 0.5 : 1 }}>
                      {financialKPIs.transactionCount}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Star sx={{ fontSize: isMobile ? 14 : 16 }} />
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        Meilleur jour: Jour {financialKPIs.bestProfitDay?.day || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </MetricCard>
              </motion.div>
            </Grid>
          </Grid>

          {/* Main Charts Section */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 2 : 4 }}>
            <Grid item xs={12} md={8}>
              <motion.div variants={fadeInUp}>
                <StyledCard>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'flex-start' : 'center', 
                    mb: isMobile ? 2 : 3,
                    gap: isMobile ? 1 : 0
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Timeline sx={{ color: theme.palette.primary.main, fontSize: isMobile ? 20 : 24 }} />
                      <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                        Évolution financière
                      </Typography>
                    </Box>
                    <Chip
                      icon={<Info sx={{ fontSize: isMobile ? 14 : 16 }} />}
                      label={`${chartData.length} périodes`}
                      size="small"
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                  </Box>
                  
                  <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="expensesGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey={viewMode === 'daily' ? 'day' : viewMode === 'weekly' ? 'week' : 'month'}
                        tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 12 }}
                        interval={isMobile ? 'preserveStartEnd' : 0}
                      />
                      <YAxis 
                        tickFormatter={(value) => formatCompactCurrency(value)}
                        tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 60 : 80}
                      />
                      <Tooltip content={renderTooltipContent} />
                      <Legend 
                        wrapperStyle={{ paddingTop: 20 }}
                        formatter={(value) => (
                          <span style={{ color: theme.palette.text.primary, fontSize: isMobile ? 12 : 14 }}>{value}</span>
                        )}
                      />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={theme.palette.primary.main}
                        fill="url(#revenueGradient)"
                        strokeWidth={2}
                        name="Revenus"
                      />
                      <Area
                        type="monotone"
                        dataKey="expenses"
                        stroke="#F59E0B"
                        fill="url(#expensesGradient)"
                        strokeWidth={2}
                        name="Dépenses"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </StyledCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div variants={fadeInUp}>
                <StyledCard sx={{ height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 2 : 3 }}>
                    <PieChartIcon sx={{ color: theme.palette.secondary.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                      Répartition des dépenses
                    </Typography>
                  </Box>
                  
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <PieChart>
                      <Pie
                        data={expenseCategories}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 40 : 60}
                        outerRadius={isMobile ? 70 : 90}
                        paddingAngle={5}
                        dataKey="value"
                        labelLine={false}
                        label={({ name, percent }) => isMobile ? `${(percent * 100).toFixed(0)}%` : `${(percent * 100).toFixed(0)}%`}
                      >
                        {expenseCategories.map((entry, index) => (
                          <Cell 
                            key={index} 
                            fill={entry.color || chartColors[index % chartColors.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{
                          background: '#1A2B3C',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          padding: isMobile ? 6 : 8,
                          fontSize: isMobile ? 12 : 14
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <Box sx={{ mt: isMobile ? 2 : 3 }}>
                    {expenseCategories.map((category, index) => (
                      <Box key={index} sx={{ mb: isMobile ? 1.5 : 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: isMobile ? 10 : 12, height: isMobile ? 10 : 12, borderRadius: '4px', bgcolor: category.color }} />
                            <Typography variant="caption" sx={{ fontSize: isMobile ? 11 : 12 }}>
                              {category.name}
                            </Typography>
                          </Box>
                          <Typography variant="caption" fontWeight={600}>
                            {formatCurrency(category.value)}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={(category.value / financialKPIs.totalExpenses) * 100}
                          sx={{ 
                            height: isMobile ? 4 : 6, 
                            borderRadius: 3,
                            bgcolor: alpha(category.color, 0.1),
                            '& .MuiLinearProgress-bar': {
                              bgcolor: category.color,
                              borderRadius: 3
                            }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </StyledCard>
              </motion.div>
            </Grid>
          </Grid>

          {/* Additional Charts */}
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <StyledCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 2 : 3 }}>
                    <BarChartIcon sx={{ color: theme.palette.info.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                      Comparaison
                    </Typography>
                  </Box>
                  
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey={viewMode === 'daily' ? 'day' : 'week'} 
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        interval={isMobile ? 'preserveStartEnd' : 0}
                      />
                      <YAxis 
                        tickFormatter={(value) => formatCompactCurrency(value)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 60 : 80}
                      />
                      <Tooltip content={renderTooltipContent} />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? 12 : 14 }}
                        formatter={(value) => (
                          <span style={{ fontSize: isMobile ? 12 : 14 }}>{value}</span>
                        )}
                      />
                      <Bar 
                        dataKey="revenue" 
                        fill={theme.palette.primary.main} 
                        name="Revenus"
                        radius={[4, 4, 0, 0]}
                        barSize={isMobile ? 20 : 30}
                      />
                      <Bar 
                        dataKey="expenses" 
                        fill="#F59E0B" 
                        name="Dépenses"
                        radius={[4, 4, 0, 0]}
                        barSize={isMobile ? 20 : 30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </StyledCard>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <StyledCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: isMobile ? 2 : 3 }}>
                    <ShowChart sx={{ color: theme.palette.success.main, fontSize: isMobile ? 20 : 24 }} />
                    <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                      Marge Bénéficiaire
                    </Typography>
                  </Box>
                  
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                      <XAxis 
                        dataKey={viewMode === 'daily' ? 'day' : 'week'} 
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        interval={isMobile ? 'preserveStartEnd' : 0}
                      />
                      <YAxis 
                        yAxisId="left" 
                        tickFormatter={(value) => formatCompactCurrency(value)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 60 : 80}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        tickFormatter={(value) => `${value}%`}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 40 : 60}
                      />
                      <Tooltip content={renderTooltipContent} />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? 12 : 14 }}
                        formatter={(value) => (
                          <span style={{ fontSize: isMobile ? 12 : 14 }}>{value}</span>
                        )}
                      />
                      <Bar 
                        yAxisId="left"
                        dataKey="profit" 
                        fill={theme.palette.success.main} 
                        name="Bénéfice"
                        radius={[4, 4, 0, 0]}
                        barSize={isMobile ? 20 : 30}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="margin"
                        stroke={theme.palette.warning.main}
                        strokeWidth={2}
                        name="Marge %"
                        dot={{ r: isMobile ? 3 : 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </StyledCard>
              </motion.div>
            </Grid>
          </Grid>

          {/* Footer */}
          <motion.div variants={fadeInUp}>
            <Box sx={{ 
              mt: isMobile ? 2 : 4, 
              p: isMobile ? 1.5 : 2,
              background: '#FFFFFF',
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? 1 : 0
            }}>
              <Typography variant="caption" sx={{ color: '#9AA6B2', fontSize: isMobile ? 10 : 12 }}>
                Dernière mise à jour: {format(new Date(), 'dd/MM/yyyy HH:mm')}
              </Typography>
              <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CheckCircle sx={{ fontSize: isMobile ? 14 : 16 }} />}
                  label={`${expenseCategories.length} catégories`}
                  size="small"
                  sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
                />
                <Chip
                  icon={<Info sx={{ fontSize: isMobile ? 14 : 16 }} />}
                  label={`${financialKPIs.transactionCount} jours analysés`}
                  size="small"
                  sx={{ bgcolor: alpha('#2196F3', 0.1), color: '#2196F3' }}
                />
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Box>
    </Box>
  );
}

export default AnalyticsPage;